import {
  ActiveCouponCard,
  BannerType,
  CouponCardAction,
  StampType,
  useToast,
} from '@pelando/components';
import { StoreDeal } from '@/domain/entities/Store';
import {
  AlgoliaEvents,
  EventCategory,
  GeneralDealEvents,
} from '@/domain/events/analytics';
import { emitTrackingEvent } from '@/infra/analytics/emitTrackingEvent';
import { getQueryId } from '@/infra/api/search/utils/trackingEvents';
import { FederatedSearchContext } from '@/infra/api/search/types/FederatedSearch';
import useDealUrl from '@/presentation/hooks/dealUrl';
import { HEADER_HEIGHT } from '@/presentation/components/Header/styles';
import { ClickoutComponentSource } from '@/presentation/services/urls';
import { useTranslation } from '@/presentation/hooks/useTranslation';
import { hasPassedThroughSearch } from '@/infra/analytics/algolia';
import { ListItem } from './styles';
import { STORE_MERCADO_LIVRE } from '..';

export type StoreCouponCardProps = {
  deal: StoreDeal;
  revealCoupons: boolean;
  stamp: StampType;
  currentUserId?: string;
};

enum CouponCardComponentName {
  MaskedButton = 'coupon_card_masked_button',
  RevealedButton = 'coupon_card_revealed_button',
  ActiveDealButton = 'coupon_card_active_deal_button',
  CopyButton = 'coupon_card_copy_button',
}

const StoreCouponCard = ({
  deal,
  revealCoupons,
  stamp,
  currentUserId,
}: StoreCouponCardProps) => {
  const { t } = useTranslation('coupons');
  const { showToast } = useToast();

  const { couponCode, store } = deal;

  const isMercadoLivre = store?.slug === STORE_MERCADO_LIVRE;

  const coupon = couponCode;
  const couponedLink = !couponCode;
  const couponHash = `#coupon=${deal.id}`;

  const componentSource =
    couponedLink && !revealCoupons
      ? ClickoutComponentSource.StorePageHiddenCoupon
      : ClickoutComponentSource.StorePageGetDiscount;

  const { url, goToClickoutUrl, clickoutUrl } = useDealUrl({
    deal,
    storeUrl: deal.store?.url,
    bodyCoupon: coupon,
    couponHash,
    componentSource,
    showCouponOnDealPage: true,
    currentUserId,
    queryId: hasPassedThroughSearch()
      ? getQueryId(FederatedSearchContext.Store)
      : undefined,
  });

  const emitClickoutEvent = () => {
    emitTrackingEvent({
      category: EventCategory.Algolia,
      name: AlgoliaEvents.ClickOut,
      extra: {
        deal_id: deal.id,
      },
    });
  };

  const handleCommand = ({ type }: { type: CouponCardAction }) => {
    switch (type) {
      case CouponCardAction.MASKED_CLICK:
        emitClickoutEvent();

        if (isMercadoLivre) {
          setTimeout(() => {
            window.location.href = url;
            window.location.reload();
          }, 100);
        } else if (goToClickoutUrl) {
          setTimeout(() => {
            window.open(url, '_blank');
          }, 100);
          goToClickoutUrl();
        }
        break;

      case CouponCardAction.SEE_REVEALED_DEAL:
        emitClickoutEvent();
        if (goToClickoutUrl) {
          goToClickoutUrl();
        }
        break;

      case CouponCardAction.CLICK_TO_COPY:
        emitTrackingEvent({
          category: EventCategory.GeneralDeal,
          name: GeneralDealEvents.ClickToCopyCouponCode,
          extra: { component_name: CouponCardComponentName.CopyButton },
        });
        showToast({
          titleText: t('copy-card-action-show-toast-sucess-title'),
          topPosition: HEADER_HEIGHT,
          text: t('copy-card-action-show-toast-sucess-text'),
          bannerType: BannerType.SUCCESS,
        });
        break;

      case CouponCardAction.ACTIVATE_DEAL:
        emitClickoutEvent();
        if (clickoutUrl) {
          setTimeout(() => {
            window.open(clickoutUrl, '_blank');
          }, 100);
        }
        break;

      default:
        break;
    }
  };

  return (
    <ListItem>
      <ActiveCouponCard
        key={deal.id}
        id={deal.id}
        stampType={stamp}
        value={deal.discountFixed || deal.discountPercentage || deal.price || 0}
        code={deal.couponCode || ''}
        title={deal.title}
        revealed={revealCoupons}
        actions={handleCommand}
        copyButtonLabel={t('copy-button-label')}
        ariaLabelIcon={t('aria-label-icon')}
        MaskedButtonLabel={t('masked-button-label')}
        ActivateButtonLabel={t('activateButtonLabel')}
      />
    </ListItem>
  );
};

export default StoreCouponCard;
